import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { bombasColors } from 'Styles/colors';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import { useBreakpoint } from '../../util/Breakpoint.context';
import RichTextPropTypes from '../../prop-types/rich-text';
import Loader from '../Loader/Loader';

import * as Styled from './CardContainer.styled';
import { CardContainerFragment } from './CardContainer.gql';

const ImageCard = lazy(() => import('../ImageCard/ImageCard.pim'));
const ContentCard = lazy(() => import('../ContentCard/ContentCard.pim'));
const TextCard = lazy(() => import('../TextCard/TextCard'));

const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) =>
      node.content[0].value ? <p>{children}</p> : <></>
  }
};

/**
 * A container for various cards in the Content System.
 *
 * It controls the the card width and lets cards wrap or render in a carousel
 */
const CardContainer = ({
  header,
  subheader,
  cards,
  cardWidth,
  headerAlignment,
  overflowProperties,
  backgroundColor,
  hasTopPadding,
  ...rest
}) => {
  const { small, medium, large } = useBreakpoint();

  const cardsMap = {
    ImageCard,
    ContentCard,
    TextCard
  };

  /**
   * Returns the column spans and shared style sizes for card sizes based on breakpoint
   *
   * If a case has a mediumWidth, it will apply to medium screens only
   *
   * Otherwise, a largeWidth will apply to medium and large screens
   */
  const getStyles = () => {
    switch (cardWidth) {
      case 'XS':
        return {
          colSpan: {
            carousel: {
              smallWidth: 6,
              mediumWidth: 3,
              largeWidth: 2
            },
            wrap: {
              smallWidth: 4,
              mediumWidth: 3,
              largeWidth: 2
            }
          },
          styleSize: {
            small: 'mini',
            medium: 'mini',
            large: 'mini'
          }
        };
      case 'S':
        return {
          colSpan: {
            carousel: {
              smallWidth: 6,
              mediumWidth: 2.5,
              largeWidth: 2
            },
            wrap: {
              smallWidth: 4,
              /**
               * Medium and large screen sizes for 'S' use a 10 column grid, per design spec
               */
              mediumWidth: 2,
              largeWidth: 2
            }
          },
          styleSize: {
            small: 'mini',
            medium: 'mini',
            large: 'mini'
          }
        };
      case 'M':
        return {
          colSpan: {
            carousel: {
              smallWidth: 12,
              mediumWidth: 4,
              largeWidth: 3
            },
            wrap: {
              smallWidth: 6,
              mediumWidth: 3,
              largeWidth: 3
            }
          },
          styleSize: {
            small: 'regular',
            medium: 'mini',
            large: 'regular'
          }
        };
      case 'L':
        return {
          colSpan: {
            carousel: {
              smallWidth: 12,
              mediumWidth: 6,
              largeWidth: 4
            },
            wrap: {
              smallWidth: 12,
              mediumWidth: 4,
              largeWidth: 4
            }
          },
          styleSize: {
            small: 'mega',
            medium: 'regular',
            large: 'regular'
          }
        };
      case 'XL':
        return {
          colSpan: {
            carousel: {
              smallWidth: 12,
              mediumWidth: 6,
              largeWidth: 6
            },
            wrap: {
              smallWidth: 12,
              mediumWidth: 6,
              largeWidth: 6
            }
          },
          styleSize: {
            small: 'mega',
            medium: 'mega',
            large: 'mega'
          }
        };
      default:
        return {
          colSpan: {
            carousel: {
              smallWidth: 12,
              mediumWidth: 6,
              largeWidth: 6
            },
            wrap: {
              smallWidth: 12,
              mediumWidth: 6,
              largeWidth: 6
            }
          },
          styleSize: {
            small: 'mega',
            medium: 'mega',
            large: 'mega'
          }
        };
    }
  };

  const {
    colSpan: { carousel, wrap },
    styleSize
  } = getStyles();

  /**
   * The column width retrieved from getColSpan, based on screen size
   */
  const currentColWidth = large
    ? 'largeWidth'
    : medium
    ? 'mediumWidth'
    : 'smallWidth';

  const currentResponsiveStyleSize = large
    ? styleSize.large
    : medium
    ? styleSize.medium
    : styleSize.small;

  /**
   * The 'S' card width uses a 10 column grid on medium and large screens for overflowProperties === Wrap.
   */
  const totalColsInGrid = cardWidth === 'S' && (medium || large) ? 10 : 12;

  /**
   * Compares the total amount of columns a list of cards would take up to the amount in the grid
   *
   * If there are 5 cards and cardWidth='M', it would be 15 columns total
   */
  const isOverflowed = cards.length * wrap[currentColWidth] > totalColsInGrid;

  const isCarousel = overflowProperties === 'CAROUSEL' && isOverflowed;

  const isContainerCentered =
    overflowProperties === 'WRAP' && headerAlignment === 'CENTER';

  /**
   * Only display carousel if Carousel is selected and the column width of all the cards is greater than the grid columns
   */
  const Wrapper = isCarousel
    ? Styled.Carousel
    : isContainerCentered
    ? Styled.Center
    : React.Fragment;

  const CenteredCardWrapper = isContainerCentered
    ? Styled.CenteredCardWrapper
    : React.Fragment;

  const carouselProps = isCarousel
    ? {
        numSlides: {
          small: totalColsInGrid / carousel.smallWidth,
          medium:
            totalColsInGrid / (carousel.mediumWidth || carousel.largeWidth),
          large: totalColsInGrid / carousel.largeWidth
        },
        withPeeking: small && !large,
        withPadding: isCarousel && !large,
        withArrowButtonVerticalOffset: false
      }
    : {};

  const bgColor =
    convertColorToThemeHexCode(backgroundColor) ||
    convertColorToThemeHexCode('white');

  /**
   * If we get data from contentful, call documentToReactComponents
   *
   * Sometimes contentful sends an empty <p/> tag in the response. This block will filter through any blank nodes and only render those that have children props
   */
  const richText =
    subheader && Object.keys(subheader).length > 0
      ? documentToReactComponents(subheader, richTextOptions)
      : [];
  const validatedRichTextContent = richText.filter(
    (child) => child.props.children
  );

  return (
    <Styled.CardContainer
      $backgroundColor={bgColor}
      $isBleeding={isCarousel && !large}
      hasTopPadding={hasTopPadding}
      {...rest}
    >
      {(header || validatedRichTextContent.length > 0) && (
        <Styled.CopyContainer
          $headerAlignment={headerAlignment}
          $isBleeding={isCarousel && !large}
          $backgroundColor={bgColor}
        >
          {header && <Styled.Header>{header}</Styled.Header>}
          {validatedRichTextContent.length > 0 ? (
            <Styled.SubHeader>{richText}</Styled.SubHeader>
          ) : null}
        </Styled.CopyContainer>
      )}
      <Styled.CardLayout
        $totalColsInGrid={totalColsInGrid}
        $isContainerCentered={isContainerCentered}
        $totalColsFromCards={cards.length * wrap[currentColWidth]}
      >
        <Wrapper {...carouselProps}>
          {cards.map((card, i) => {
            const Module = cardsMap[card.__typename];

            if (cardsMap[card.__typename]) {
              return (
                <Loader key={card.id || i}>
                  <CenteredCardWrapper
                    {...(isContainerCentered
                      ? { $span: wrap[currentColWidth] }
                      : {})}
                  >
                    <Module
                      {...card}
                      gridSpan={wrap[currentColWidth]}
                      responsiveStyleSize={currentResponsiveStyleSize}
                      containerBackgroundColor={bgColor}
                    />
                  </CenteredCardWrapper>
                </Loader>
              );
            }

            return null;
          })}
        </Wrapper>
      </Styled.CardLayout>
    </Styled.CardContainer>
  );
};

CardContainer.propTypes = {
  /**
   * The title of the container
   */
  header: PropTypes.string,
  /**
   * Align the header to the left or center
   */
  headerAlignment: PropTypes.oneOf(['LEFT', 'CENTER']),
  /**
   * Any supporting text for the header
   *
   * This field is a rich text document supplied from contentful
   */
  subheader: PropTypes.shape(RichTextPropTypes),
  /**
   * An array of cards
   */
  cards: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Various width options for the cards
   */
  cardWidth: PropTypes.oneOf(['XS', 'S', 'M', 'L', 'XL']),
  /**
   * Makes the container either wrap or behave as a carousel
   */
  overflowProperties: PropTypes.oneOf(['WRAP', 'CAROUSEL']),
  /**
   * The background color of the card container
   */
  backgroundColor: PropTypes.oneOf(bombasColors),
  /**
   * Removes top padding of the card container
   */
  hasTopPadding: PropTypes.bool
};

CardContainer.fragment = CardContainerFragment;

CardContainer.defaultProps = {
  header: '',
  headerAlignment: 'LEFT',
  subheader: {},
  cards: [],
  cardWidth: 'XL',
  overflowProperties: 'WRAP',
  backgroundColor: 'white',
  hasTopPadding: true
};

export default CardContainer;
